/* tslint:disable:no-console */

import {WasmModule} from '@/emulator/WasmModule';
import DebuggingInfo from '@/emulator/DebuggingInfo';
import ProjectFile from '@/emulator/ProjectFile';
import EmulatorInfo from '@/emulator/EmulatorInfo';
import {CompilerOutput} from '@/emulator/CompilerOutput';
import emulatorVersions from '../emulatorVersions';

function assert(expr: number | boolean) : void {
    if (typeof expr === 'boolean') {
        if (!expr as boolean)
            throw new Error('Emulator execution error.');
    } else {
        if (expr !== 0)
            throw new Error('Emulator execution error.');
    }
}

export class Emulator {
    private module: WasmModule;

    constructor(module: WasmModule) {
        this.module = module;
    }

    // noinspection JSUnusedGlobalSymbols
    public init(resetMemory = true) : void { assert(this.module._init(resetMemory)); }

    public mainLoop(errorCallback: () => void) : void {
        const functionPtr = this.module.addFunction(errorCallback, 'v');
        this.module._main_loop(functionPtr);
    }

    public stopMainLoop() : void { assert(this.module._stop_main_loop()); }

    public step() : void { this.module._step(); }

    public nextInstructionIsSubroutine() : boolean { return this.module._next_is_subroutine(); }

    public frame() : void { this.module._frame(); }

    public destroy() : void { assert(this.module._destroy()); }

    public sendKeypress(key: number) : void { assert(this.module._send_keypress(key)); }

    public setJoystickState(state: number) : void { assert(this.module._set_joystick(state)); }

    public screenUpdate() : void { assert(this.module._screen_update()); }

    public reset() : void { assert(this.module._reset()); }

    public swapBreakpoint(filename: string, line: number) : boolean {
        const cFilename = window.allocate(window.intArrayFromString(filename), 'i8', window.ALLOC_NORMAL);
        const r = this.module._swap_breakpoint(cFilename, line);
        window._free(cFilename);
        if (r === 0)
            console.warn(`Not a breakable line (${filename}:${line})`);
        return r !== 0;
    }

    // noinspection JSUnusedGlobalSymbols
    public removeBreakpoint(filename: string, line: number) : void {
        const cFilename = window.allocate(window.intArrayFromString(filename), 'i8', window.ALLOC_NORMAL);
        const r = this.module._remove_breakpoint(cFilename, line);
        window._free(cFilename);
        assert(r);
    }

    public addBreakpointListener(listener: () => void) : void {
        const functionPtr = this.module.addFunction(listener, 'v');
        assert(this.module._add_breakpoint_listener(functionPtr));
    }

    public addBreakAtEndOfFrame() : void {
        assert(this.module._break_at_end_of_frame());
    }

    public setBreakNext() : void {
        assert(this.module._break_next());
    }

    public debuggerState(memoryBlock: number) : EmulatorInfo {
        const sz = 128 * 1024;
        const buffer = this.module._malloc(sz);
        const result = this.module._emulator_state(memoryBlock, buffer, sz);
        let jsonTxt;
        if (result === 0) {
            jsonTxt = window.UTF8ToString(buffer);
            window._free(buffer);
        } else {
            window.free(buffer);
            throw new Error('Not enough memory.');
        }
        return JSON.parse(jsonTxt) as EmulatorInfo;
    }

    public loadMemory(array: number[]) : void {
        const buffer = this.module._malloc(array.length);
        window.writeArrayToMemory(array, buffer);
        this.module._load_memory(buffer, array.length);
        window._free(buffer);
    }

    public loadDebuggingInfo(dbg: DebuggingInfo) : void {
        const ptr = this.module._debugging_info_create();
        for (const d of dbg.locations) {
            const filename = window.allocate(window.intArrayFromString(dbg.files[d[1]]), 'i8', window.ALLOC_NORMAL);
            this.module._debugging_info_add(ptr, d[0], filename, d[2]);
            window._free(filename);
        }
        this.module._debugging_info_load_and_free(ptr);
    }

    // noinspection JSUnusedGlobalSymbols
    public setCanvas(canvasId: string) : void {
        this.module.canvas = (document.getElementById(canvasId) as HTMLCanvasElement);
    }

    public compile(project: ProjectFile[]) : CompilerOutput {
        // create input
        const input = this.module._input_new();
        if (process.env.NODE_ENV === 'development') {
            console.log('Current project:');
            console.log(project);
        }
        for (const file of project) {
            if (file.name.endsWith('.def') || file.name.endsWith('.s')) {
                const cFilename = window.allocate(window.intArrayFromString(file.name), 'i8', window.ALLOC_NORMAL);
                const cCode = window.allocate(window.intArrayFromString(file.content), 'i8', window.ALLOC_NORMAL);
                this.module._input_add_file(input, cFilename, cCode);
                window._free(cFilename);
                window._free(cCode);
            }
        }

        // compile
        const output = this.module._compile_input(input);
        this.module._input_free(input);

        // convert result to json
        let mb = 1;  // start with a 4 Mb buffer
        let jsonTxt: string = '';
        let result;
        do {
            const mySz = mb * 1024 * 1024;
            const myBuffer = this.module._malloc(mySz);
            result = this.module._output_to_json(output, myBuffer, mySz);
            if (result === 0) {
                jsonTxt = window.UTF8ToString(myBuffer);
            }
            window._free(myBuffer);
            mb *= 2;
            if (mb >= 64) {
                throw new Error('Compilation is using too much memory.');
            }
        } while (result !== 0);

        const json = JSON.parse(jsonTxt) as CompilerOutput;

        // get debugging information
        const sz = 8 * 1024 * 1024;
        const buffer = this.module._malloc(sz);
        this.module._debug_to_json(output, buffer, sz);
        jsonTxt = window.UTF8ToString(buffer);
        window._free(buffer);
        json.debuggingInfo = JSON.parse(jsonTxt);

        this.module._output_free(output);
        return json;
    }
}

// {{{ emulator loader

let emulatorInstance: Emulator | undefined;
let emulatorVersion: string = '';

export default async function emulator(version: string, canvasId?: string): Promise<Emulator> {
    if (version !== emulatorVersion && emulatorVersion !== '')
        window.location.reload();

    if (!(emulatorVersions as string[]).includes(version))
        throw new Error(`Compiler/emulator version ${version} is not supported.`);
    const setCanvasId = (id: string | undefined) => {
        if (id) {
            window.Module.canvas = document.getElementById(canvasId!) as HTMLCanvasElement;
        } else {
            window.Module.canvas = undefined;
        }
    };

    return new Promise(async (resolve, reject) => {
        if (emulatorInstance) {
            setCanvasId(canvasId);
            resolve(emulatorInstance);
        } else {
            try {
                const response = await fetch(`/retrolab-${version}.wasm`);
                const buffer = await response.arrayBuffer();
                window.Module = {} as WasmModule;
                setCanvasId(canvasId);
                window.Module.wasmBinary = buffer;
                window.Module.onAbort = (e: Error) => {
                    console.log(e);
                    reject(e);
                };
                window.Module.onRuntimeInitialized = () => {
                    emulatorInstance = new Emulator(window.Module);
                    emulatorVersion = version;
                    resolve(emulatorInstance);
                    console.log(window.Module);
                };
                const script = document.createElement('script');
                script.src = `/retrolab-${version}.js`;
                script.onload = () => console.log('Emscripten boilerplate loaded.');
                document.body.appendChild(script);
                window.Module.script = script;
            } catch (e) {
                reject(e);
            }
        }
    });
}

// }}}

// vim:foldmethod=marker
